import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import {
  Target,
  Calendar,
  TrendingUp,
  CheckCircle,
  Lightbulb,
  Share2,
  Printer,
  Mail,
  Sparkles,
} from 'lucide-react';
import { ReportSection } from '@/components/ReportSection';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { storage } from '@/lib/storage';
import { ProductivityReport } from '@/lib/api';
import { funnelAnalytics } from '@/services/funnel-analytics';

export default function ReportPage() {
  const navigate = useNavigate();
  const [report, setReport] = useState<ProductivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    loadReport();
    // Hide confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const loadReport = () => {
    const userId = storage.getUserId();
    const savedReport = storage.getReport();

    if (!userId || !savedReport) {
      navigate('/goals');
      return;
    }

    setReport(savedReport);
    storage.setCurrentStep('report');
    setLoading(false);

    // Track funnel step: Report viewed (final conversion step)
    funnelAnalytics.trackReportViewed();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const userId = storage.getUserId();
    const url = `${window.location.origin}/report/${userId}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCreateAccount = () => {
    const userId = storage.getUserId();
    // Redirect to app with soft user ID for conversion
    window.location.href = `https://app.mybestday.ai?convertFrom=${userId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Loading your productivity plan..." size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="card-simple max-w-md text-center">
          <p className="text-error-600 mb-4">Report not found</p>
          <button onClick={() => navigate('/goals')} className="btn-primary">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Confetti Animation */}
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={500}
        />
      )}

      <div className="section">
        <div className="container-narrow">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-20 h-20 bg-success-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-white" size={40} />
            </div>
            <h1 className="heading">Your Productivity Plan Is Ready!</h1>
            <p className="subheading">
              We've analyzed your goals and created a personalized plan to help you achieve them
              faster.
            </p>
          </motion.div>

          {/* Email Confirmation */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="success-message mb-6 flex items-start gap-3"
          >
            <Mail size={20} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Report emailed to {storage.getEmail()}</p>
              <p className="text-sm">Check your inbox for a copy of this plan.</p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <div className="card mb-8 no-print">
            <div className="flex flex-wrap gap-3">
              <button onClick={handleShare} className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none">
                <Share2 size={18} />
                {copySuccess ? 'Link Copied!' : 'Share'}
              </button>
              <button onClick={handlePrint} className="btn-secondary flex items-center gap-2 flex-1 sm:flex-none">
                <Printer size={18} />
                Print
              </button>
              <button onClick={handleCreateAccount} className="btn-primary flex-1 sm:flex-auto">
                <Sparkles size={18} />
                <span>Save to My Best Day</span>
              </button>
            </div>
          </div>

          {/* Priority Goals */}
          <ReportSection title="Your Goals (Prioritized)" icon={Target}>
            <div className="space-y-4">
              {report.goals && report.goals.length > 0 ? report.goals
                .sort((a, b) => b.priority - a.priority)
                .map((goal, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="card-simple"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white">
                        {index + 1}
                      </div>
                      <div className="flex-grow">
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">
                          {goal.description}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">{goal.reasoning}</p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-gray-500">
                            <strong>Priority:</strong> {goal.priority}/100
                          </span>
                          <span className="text-gray-500">
                            <strong>Est. Time:</strong> {goal.estimatedTimeToComplete}
                          </span>
                        </div>
                        {goal.dependencies.length > 0 && (
                          <div className="mt-2 text-sm text-gray-600">
                            <strong>Dependencies:</strong> {goal.dependencies.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )) : (
                  <p className="text-gray-600">No goals data available.</p>
                )}
            </div>
          </ReportSection>

          {/* Timeline */}
          <ReportSection title="Your Timeline" icon={Calendar}>
            <div className="space-y-4">
              {report.recommendations.timeline.map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card-simple border-l-4 border-primary-600"
                >
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">{phase.phase}</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>Duration:</strong> {phase.duration}
                  </p>
                  <div className="mb-3">
                    <strong className="text-sm text-gray-700">Goals:</strong>
                    <ul className="list-disc list-inside text-sm text-gray-600 ml-2 mt-1">
                      {phase.goals.map((goal, i) => (
                        <li key={i}>{goal}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Milestones:</strong>
                    <ul className="list-disc list-inside text-sm text-gray-600 ml-2 mt-1">
                      {phase.milestones.map((milestone, i) => (
                        <li key={i}>{milestone}</li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </ReportSection>

          {/* Habits */}
          <ReportSection title="Recommended Habits" icon={TrendingUp}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Daily Habits</h3>
                <ul className="space-y-2">
                  {report.recommendations.dailyHabits.map((habit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="text-success-600 flex-shrink-0 mt-0.5" size={18} />
                      <span>{typeof habit === 'string' ? habit : (habit as any).name || (habit as any).habit || (habit as any).description || ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">Weekly Habits</h3>
                <ul className="space-y-2">
                  {report.recommendations.weeklyHabits.map((habit, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="text-success-600 flex-shrink-0 mt-0.5" size={18} />
                      <span>{typeof habit === 'string' ? habit : (habit as any).name || (habit as any).habit || (habit as any).description || ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ReportSection>

          {/* Next Actions */}
          <ReportSection title="Immediate Next Actions" icon={Target}>
            <div className="space-y-4">
              {Object.entries(report.recommendations.nextActions).map(([goal, actions], index) => (
                <div key={index} className="card-simple bg-blue-50 border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-3">{goal}</h3>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700">
                    {actions.map((action, i) => (
                      <li key={i}>{action}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </ReportSection>

          {/* Strategies */}
          <ReportSection title="Success Strategies" icon={Lightbulb}>
            <ul className="space-y-3">
              {report.recommendations.strategies.map((strategy, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-3 p-4 bg-accent-50 border border-accent-200 rounded-lg"
                >
                  <Lightbulb className="text-accent-600 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-800">{typeof strategy === 'string' ? strategy : (strategy as any).strategy || (strategy as any).description || (strategy as any).name || ''}</span>
                </motion.li>
              ))}
            </ul>
          </ReportSection>

          {/* Custom Advice */}
          <ReportSection title="Personalized Advice" icon={Sparkles}>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {report.customAdvice}
              </p>
            </div>
          </ReportSection>

          {/* CTA to Create Account */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card bg-gradient-to-br from-primary-600 to-blue-600 text-white no-print mt-12"
          >
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to Make This Happen?
              </h2>
              <p className="text-lg text-primary-100 mb-6">
                Save your plan to My Best Day and get AI-powered daily guidance to achieve your
                goals faster.
              </p>
              <button onClick={handleCreateAccount} className="btn-secondary btn-large">
                <Sparkles size={20} />
                <span>Create Free Account</span>
              </button>
              <p className="mt-4 text-sm text-primary-200">
                Free tier available forever. No credit card required.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
